import { useEffect, useRef } from 'react';

export interface DevCredentials {
  email: string;
  password: string;
}

/**
 * Sign-in forms currently on screen. A Set rather than a window event so
 * `fillDevCredentials` can tell the caller whether anything was listening —
 * a panel that closes as though it signed in when no form exists would be
 * lying.
 */
const forms = new Set<(credentials: DevCredentials) => void>();

/** Fills and submits every sign-in form on screen. False when there is none. */
export function fillDevCredentials(credentials: DevCredentials): boolean {
  for (const fill of forms) fill(credentials);
  return forms.size > 0;
}

/**
 * Lets the dev login panel drive this form: the handler fills the fields
 * and submits them, the way clicking the button would.
 *
 * The handler is held in a ref so a fresh closure on every render does not
 * re-subscribe, and so the panel always reaches the current one.
 */
export function useDevCredentials(handler: (credentials: DevCredentials) => void): void {
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    const fill = (credentials: DevCredentials) => latest.current(credentials);
    forms.add(fill);
    return () => {
      forms.delete(fill);
    };
  }, []);
}
